import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { api } from "../lib/api";
import { FontOut, FontsListResponse } from "../types";
import { useDesign } from "../state/designStore";

function useFontPreviewImage(font: FontOut): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    api
      .getArrayBuffer(`/api/fonts/${encodeURIComponent(font.id)}/preview.png`)
      .then((buffer) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([buffer], { type: "image/png" }));
        setUrl(objectUrl);
      })
      .catch(() => {
        /* preview is best-effort - selection still works without it */
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [font.id]);

  return url;
}

function FontRow({ font, isActive, onSelect }: { font: FontOut; isActive: boolean; onSelect: () => void }) {
  const previewUrl = useFontPreviewImage(font);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center justify-between gap-3 rounded-2xl px-4 py-3.5 text-right transition-colors ${
        isActive ? "bg-tg-button/15 border border-tg-button" : "glass-pill border border-transparent"
      }`}
    >
      <span className="flex flex-col items-start gap-1.5 min-w-0">
        <span className="text-xs text-tg-hint">{font.label}</span>
        {previewUrl ? (
          <img src={previewUrl} alt={font.label} className="h-6" />
        ) : (
          <span className="h-6 w-24 rounded bg-white/10 animate-pulse" />
        )}
      </span>
      {isActive && <Check className="text-tg-button shrink-0" size={20} strokeWidth={2.5} />}
    </button>
  );
}

export function FontPickerModal({
  value,
  onChange,
  onClose,
}: {
  value: string | null;
  onChange: (fontId: string | null) => void;
  onClose: () => void;
}) {
  const [fonts, setFonts] = useState<FontOut[] | null>(null);
  const { setOverlayClose } = useDesign();

  useEffect(() => {
    api
      .get<FontsListResponse>("/api/fonts")
      .then((res) => setFonts(res.fonts))
      .catch(() => setFonts([]));
  }, []);

  // Registers this modal's close function as the shared "something is
  // open on top of the screen" handle - see ColorPickerModal for the
  // full explanation of why this replaced a direct Telegram-SDK-level
  // back button takeover.
  useEffect(() => {
    setOverlayClose(() => onClose);
    return () => setOverlayClose(null);
  }, [onClose, setOverlayClose]);

  const pick = (fontId: string | null) => {
    onChange(fontId);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-md glass-card rounded-b-none max-h-[75vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <h2 className="font-bold">اختر الخط</h2>
          <button type="button" onClick={onClose} aria-label="إغلاق" className="w-8 h-8 flex items-center justify-center">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-3 flex flex-col gap-2">
          {fonts === null && <p className="text-xs text-tg-hint p-2">جاري تحميل الخطوط...</p>}
          {fonts && fonts.length === 0 && (
            <p className="text-xs text-tg-hint p-2">لا توجد خطوط إضافية - يُستخدم خط القالب الافتراضي</p>
          )}

          {fonts && (
            <button
              type="button"
              onClick={() => pick(null)}
              className={`w-full flex items-center justify-between gap-3 rounded-2xl px-4 py-3.5 text-right transition-colors ${
                value === null ? "bg-tg-button/15 border border-tg-button" : "glass-pill border border-transparent"
              }`}
            >
              <span className="flex flex-col items-start gap-1">
                <span className="text-xs text-tg-hint">افتراضي</span>
                <span className="text-xl">Aa</span>
              </span>
              {value === null && <Check className="text-tg-button shrink-0" size={20} strokeWidth={2.5} />}
            </button>
          )}

          {fonts?.map((f) => (
            <FontRow key={f.id} font={f} isActive={value === f.id} onSelect={() => pick(f.id)} />
          ))}
        </div>
      </div>
    </div>
  );
}
